import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface DragResizeSidePanelProps {
  open: boolean;
  /** Optional close handler — wires Esc-to-close. */
  onClose?: () => void;
  /**
   * localStorage key the width is persisted under.
   * Convention: `atek_<feature>_panel_width`.
   */
  storageKey: string;
  /** Min width in px. Default 350. */
  minWidth?: number;
  /** Max width as a fraction of the container width (0–1). Default 0.6. */
  maxFraction?: number;
  /** Default width as a fraction of the container width when nothing is persisted. Default 0.4. */
  defaultFraction?: number;
  /** Container ref for measuring; falls back to viewport width. */
  containerRef: React.RefObject<HTMLElement | null>;
  className?: string;
  children: React.ReactNode;
}

/**
 * Drag-resize side panel — the V2 primary detail/edit slot.
 *
 * Renders the drag handle + the panel container. Caller is responsible for the
 * outer flex row and providing the container ref. The panel width is persisted
 * to localStorage and clamped to [minWidth, container × maxFraction].
 *
 * Usage:
 *   const containerRef = useRef<HTMLDivElement>(null);
 *   <div ref={containerRef} className="flex min-h-[calc(100vh-240px)]">
 *     <div className="flex-1 min-w-0 overflow-auto">{tabContent}</div>
 *     <DragResizeSidePanel
 *       open={panelOpen}
 *       onClose={closePanel}
 *       storageKey="atek_invoices_panel_width"
 *       containerRef={containerRef}
 *     >
 *       <DetailPanel ... />
 *     </DragResizeSidePanel>
 *   </div>
 */
export function DragResizeSidePanel({
  open,
  onClose,
  storageKey,
  minWidth = 350,
  maxFraction = 0.6,
  defaultFraction = 0.4,
  containerRef,
  className,
  children,
}: DragResizeSidePanelProps) {
  const [width, setWidth] = useState<number>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });
  const dragging = useRef(false);

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragging.current = true;
      const startX = e.clientX;
      const startWidth =
        width || (containerRef.current ? containerRef.current.offsetWidth * defaultFraction : 450);

      const onMove = (ev: MouseEvent) => {
        if (!dragging.current || !containerRef.current) return;
        const delta = startX - ev.clientX;
        const containerWidth = containerRef.current.offsetWidth;
        setWidth(Math.min(Math.max(startWidth + delta, minWidth), containerWidth * maxFraction));
      };

      const onUp = () => {
        dragging.current = false;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setWidth((w) => {
          try { localStorage.setItem(storageKey, String(w)); } catch { /* storage is best-effort; ignore (private mode / quota) */ }
          return w;
        });
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [width, containerRef, storageKey, minWidth, maxFraction, defaultFraction],
  );

  // Esc closes the panel
  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="w-1.5 shrink-0 cursor-col-resize hover:bg-blue-400 active:bg-blue-500 transition-colors bg-border rounded-full my-2"
        onMouseDown={handleDragStart}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize detail panel"
      />
      <div
        className={cn(
          'shrink-0 min-h-0 h-full flex flex-col border-l bg-background overflow-hidden',
          className,
        )}
        style={{ width: width || `${defaultFraction * 100}%` }}
      >
        {children}
      </div>
    </>
  );
}
